import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, Columns2, Download, Eye, FileText, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { api } from "../../lib/api";
import { mdToHtml } from "../viewer/markdown/mdToHtml";
import { splitFrontmatter } from "./frontmatter";

const DRAFT_KEY = (docId: string) => `docustudio.draft.${docId}`;

type EditorMode = "markdown" | "preview" | "split";

export function DocEditor() {
  const { docId } = useParams({ strict: false }) as { docId: string };
  const [mode, setMode] = useState<EditorMode>("markdown");
  const [markdown, setMarkdown] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const md = useQuery({
    queryKey: ["doc-markdown", docId],
    queryFn: () => api.getDocMarkdown(docId),
  });

  useEffect(() => {
    if (!md.data) return;
    const draft = localStorage.getItem(DRAFT_KEY(docId));
    if (draft) {
      try {
        const parsed = JSON.parse(draft) as { markdown: string; savedAt: string };
        setMarkdown(parsed.markdown);
        setSavedAt(parsed.savedAt);
        setDirty(false);
        return;
      } catch {
        localStorage.removeItem(DRAFT_KEY(docId));
      }
    }
    setMarkdown(md.data);
    setSavedAt(null);
    setDirty(false);
  }, [md.data, docId]);

  const preview = useQuery({
    queryKey: ["doc-editor-preview", docId, markdown],
    queryFn: async () => mdToHtml(markdown),
    enabled: markdown.length > 0,
  });

  const frontmatter = useMemo(() => splitFrontmatter(markdown).fm, [markdown]);
  const stats = useMemo(() => {
    const body = splitFrontmatter(markdown).body;
    const words = body.trim() ? body.trim().split(/\s+/).length : 0;
    return {
      chars: markdown.length,
      words,
      lines: markdown.split(/\r?\n/).length,
      minutes: Math.max(1, Math.round(words / 200)),
    };
  }, [markdown]);

  function save() {
    const payload = { markdown, savedAt: new Date().toISOString() };
    localStorage.setItem(DRAFT_KEY(docId), JSON.stringify(payload));
    setSavedAt(payload.savedAt);
    setDirty(false);
    toast.success("Borrador Markdown guardado localmente", {
      description: "Persistencia remota pendiente de la fase de edición colaborativa.",
    });
  }

  function discardDraft() {
    localStorage.removeItem(DRAFT_KEY(docId));
    setMarkdown(md.data ?? "");
    setDirty(false);
    setSavedAt(null);
    toast.message("Borrador descartado");
  }

  function exportMarkdown() {
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${frontmatter.title ?? docId}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (md.isLoading) {
    return (
      <div className="px-6 py-6 space-y-3">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      <main className="flex flex-1 flex-col overflow-hidden">
        <header className="flex min-w-0 items-center gap-2 border-b border-(--border-subtle) bg-(--bg-elevated) px-4 py-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/docs/$docId" params={{ docId }}>
              <ChevronLeft size={14} /> Volver
            </Link>
          </Button>
          <h2 className="min-w-0 truncate text-sm font-semibold flex items-center gap-2">
            <FileText size={14} /> Editor Markdown · {frontmatter.title ?? docId}
          </h2>
          {dirty && (
            <span className="rounded-full bg-(--warning-50) px-2 py-0.5 text-[10px] text-(--warning-700)">
              cambios sin guardar
            </span>
          )}
          {savedAt && !dirty && (
            <span className="hidden text-[10px] text-(--text-tertiary) md:inline">
              borrador guardado {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <ModeButton mode={mode} value="markdown" onChange={setMode} label="Markdown">
              <FileText size={13} />
            </ModeButton>
            <ModeButton mode={mode} value="preview" onChange={setMode} label="Visual">
              <Eye size={13} />
            </ModeButton>
            <ModeButton mode={mode} value="split" onChange={setMode} label="Mixta">
              <Columns2 size={13} />
            </ModeButton>
            <Button variant="secondary" size="sm" onClick={exportMarkdown}>
              <Download size={13} /> .md
            </Button>
            <Button variant="ghost" size="sm" onClick={discardDraft} disabled={!savedAt}>
              Descartar
            </Button>
            <Button variant="primary" size="sm" onClick={save} disabled={!dirty}>
              <Save size={13} /> Guardar
            </Button>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-hidden">
            {mode === "markdown" && (
              <MarkdownPane
                markdown={markdown}
                onChange={(value) => {
                  setMarkdown(value);
                  setDirty(true);
                }}
              />
            )}
            {mode === "preview" && <PreviewPane html={preview.data?.html ?? ""} />}
            {mode === "split" && (
              <div className="grid h-full grid-cols-2 overflow-hidden">
                <MarkdownPane
                  markdown={markdown}
                  onChange={(value) => {
                    setMarkdown(value);
                    setDirty(true);
                  }}
                />
                <PreviewPane html={preview.data?.html ?? ""} framed />
              </div>
            )}
          </div>

          <aside className="hidden w-60 overflow-y-auto border-l border-(--border-subtle) bg-(--bg-elevated) px-3 py-4 text-xs lg:block">
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
              Estadísticas
            </h3>
            <ul className="space-y-1 text-(--text-secondary)">
              <li>{stats.chars} caracteres</li>
              <li>{stats.words} palabras</li>
              <li>{stats.lines} líneas</li>
              <li>~{stats.minutes} min lectura</li>
            </ul>
            <h3 className="mb-2 mt-4 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
              Frontmatter
            </h3>
            <ul className="space-y-1 text-(--text-secondary)">
              <ValidationItem ok={Boolean(frontmatter.title)} label="Título" />
              <ValidationItem ok={Boolean(frontmatter.project)} label="Proyecto" />
              <ValidationItem ok={Boolean(frontmatter.lang)} label="Idioma" />
              <ValidationItem ok={Boolean(frontmatter.visibility)} label="Visibilidad" />
            </ul>
            <h3 className="mb-2 mt-4 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
              Guardado
            </h3>
            <p className="text-(--text-secondary)">
              Esta pantalla edita el Markdown fuente y guarda borrador local. El original remoto no
              se modifica desde aquí.
            </p>
          </aside>
        </div>
      </main>
    </div>
  );
}

function ModeButton({
  mode,
  value,
  label,
  children,
  onChange,
}: {
  mode: EditorMode;
  value: EditorMode;
  label: string;
  children: React.ReactNode;
  onChange: (mode: EditorMode) => void;
}) {
  return (
    <Button
      variant={mode === value ? "secondary" : "ghost"}
      size="sm"
      onClick={() => onChange(value)}
      aria-pressed={mode === value}
    >
      {children}
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}

function MarkdownPane({
  markdown,
  onChange,
}: {
  markdown: string;
  onChange: (value: string) => void;
}) {
  return (
    <textarea
      aria-label="Editor Markdown"
      value={markdown}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      className="h-full w-full resize-none overflow-auto border-0 bg-(--bg-base) px-6 py-5 font-mono text-sm leading-6 text-(--text-primary) outline-none"
    />
  );
}

function PreviewPane({ html, framed = false }: { html: string; framed?: boolean }) {
  return (
    <div
      className={
        framed
          ? "h-full overflow-y-auto border-l border-(--border-subtle) bg-(--bg-base) px-6 py-5"
          : "h-full overflow-y-auto bg-(--bg-base) px-6 py-5"
      }
    >
      <article
        className="prose mx-auto max-w-3xl"
        data-prose
        // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is generated by our Markdown pipeline
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function ValidationItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={ok ? "text-(--success)" : "text-(--warning)"}>{`${ok ? "✓" : "!"} ${label}`}</li>
  );
}
